import * as React from "react";
import tw from "twin.macro";
import type { CSS } from "~/config/stitches";
import { styled } from "~/config/stitches";

const IconSize = {
  LARGE: "large",
  MEDIUM: "medium",
  SMALL: "small",
  XLARGE: "xlarge",
  XSMALL: "xsmall",
  XXLARGE: "xxlarge",
  XXSMALL: "xxsmall",
} as const;

type IconColor =
  | "brandAccent"
  | "caution"
  | "clovisNavy"
  | "clovisRed"
  | "critical"
  | "info"
  | "neutral"
  | "neutralLight"
  | "positive"
  | "promote"
  | "white";

type IconSizeValue = (typeof IconSize)[keyof typeof IconSize];

const IconSvg = styled("svg", {
  defaultVariants: {
    size: "medium",
  },
  variants: {
    color: {
      brandAccent: tw`text-emerald-500`,
      caution: tw`text-caution-500`,
      clovisNavy: tw`text-slate-900`,
      clovisRed: tw`text-rose-500`,
      critical: tw`text-critical-500`,
      info: tw`text-info-500`,
      neutral: tw`text-neutral-500`,
      neutralLight: tw`text-neutral-300`,
      positive: tw`text-positive-500`,
      promote: tw`text-promote-500`,
      white: tw`text-white`,
    },
    size: {
      large: tw`h-8 w-8`,
      medium: tw`h-6 w-6`,
      small: tw`h-5 w-5`,
      xlarge: tw`h-10 w-10`,
      xsmall: tw`h-4 w-4`,
      xxlarge: tw`h-12 w-12`,
      xxsmall: tw`h-3 w-3`,
    },
  },
});

const IconDiv = styled("div", {
  "& > svg": {
    height: "inherit",
    width: "inherit",
  },
  defaultVariants: {
    size: "medium",
  },
  variants: {
    color: {
      brandAccent: tw`text-emerald-500`,
      caution: tw`text-caution-500`,
      clovisNavy: tw`text-slate-900`,
      clovisRed: tw`text-rose-500`,
      critical: tw`text-critical-500`,
      info: tw`text-info-500`,
      neutral: tw`text-neutral-500`,
      neutralLight: tw`text-neutral-300`,
      positive: tw`text-positive-500`,
      promote: tw`text-promote-500`,
      white: tw`text-white`,
    },
    size: {
      large: tw`h-8 w-8`,
      medium: tw`h-6 w-6`,
      small: tw`h-5 w-5`,
      xlarge: tw`h-10 w-10`,
      xsmall: tw`h-4 w-4`,
      xxlarge: tw`h-12 w-12`,
      xxsmall: tw`h-3 w-3`,
    },
  },
});

type IconBaseProps = {
  className?: string;
  css?: CSS;
  children: React.ReactNode;
  label?: string;
  svg?: boolean;
  viewBox?: string;
  color?: IconColor;
  size?: IconSizeValue;
};

type IconProps = IconBaseProps;

const Icon = (props: IconProps) => {
  const { label, svg, ...iconProps } = props;

  if (svg) {
    return (
      <IconSvg
        {...iconProps}
        role="img"
        aria-label={label}
        aria-hidden={!label}
      >
        {props.children}
      </IconSvg>
    );
  }

  return (
    <IconDiv {...iconProps} role="img" aria-label={label} aria-hidden={!label}>
      {props.children}
    </IconDiv>
  );
};

export { Icon, IconSize };
export type { IconProps, IconSizeValue };
